import type { ExternalTableSchema } from '@packages/table-builder';

type TableColumn = NonNullable<ExternalTableSchema['columns']>[number];

/** Shared vesting-list / included / detail filter bar — keep in sync across all release item tables. */
export const releaseItemFilters: NonNullable<
  NonNullable<ExternalTableSchema['dataSource']>['filters']
> = [
  {
    name: 'createdFromDate',
    placeholder: 'Vesting Date From',
    type: 'date',
  },
  {
    name: 'createdToDate',
    placeholder: 'Vesting Date To',
    type: 'date',
  },
  {
    name: 'awardTypeId',
    placeholder: 'Award Type',
    type: 'asyncSelect',
    dataSource: {
      url: '/api/award/api/app/plan-type-category/release-type-category-list',
      labelField: 'name',
      valueField: 'id',
    },
  },
  {
    name: 'grantName',
    placeholder: 'Grant Name',
    type: 'asyncSelect',
    dataSource: {
      url: '/api/award/api/app/grant/grants',
      labelField: 'name',
      valueField: 'id',
    },
  },
  {
    name: 'settlementTypeIds',
    placeholder: 'Settlement Type',
    type: 'asyncSelect',
    dataSource: {
      url: '/api/award/api/app/option-exercise/settlement-types',
      labelField: 'displayName',
      valueField: 'id',
    },
  },
];

/** Award APIs use SearchKeyWord — never table-builder default Filter. */
export const releaseItemSearchRequestParams = {
  SearchKeyWord: 'table.searchText',
  Filter: '',
  MaxResultCount: 'table.pageSize',
  SkipCount: 'table.pageOffset',
  Sorting: 'table.sorting',
  GrantId: 'filters.grantName',
  AwardTypeId: 'filters.awardTypeId',
  FromDate: 'filters.createdFromDate',
  ToDate: 'filters.createdToDate',
  SettlementTypeIds: 'filters.settlementTypeIds',
} as const;

/** Legacy null money → '-' (never emptyDisplay: '0'). */
export const releaseFmvColumn: TableColumn = {
  field: 'fmv',
  header: 'FMV $',
  type: 'text',
  sortable: true,
  width: 180,
  align: 'center',
  format: { decimals: 4, fixed: true },
  fields: { formatAsNumber: 'true' },
};

export const releaseVestingValuesColumn: TableColumn = {
  field: 'vestingValues',
  header: 'Vesting Value $',
  type: 'text',
  sortable: true,
  width: 200,
  align: 'center',
  format: { decimals: 4, fixed: true },
  fields: { formatAsNumber: 'true' },
};

/** Legacy isNumberField — comma integer in Tag/Lozenge default (gray badge). */
export const releaseVestingUnitColumn: TableColumn = {
  field: 'vestingUnit',
  header: 'Unit',
  type: 'number',
  sortable: true,
  width: 180,
  align: 'center',
  format: { commas: true },
};

const releaseParticipantDetailColumn: TableColumn = {
  field: 'participantDetail',
  header: 'Participant Details',
  sortable: true,
  width: 250,
};

const releaseVestDateColumn: TableColumn = {
  field: 'vestingDate',
  header: 'Vest Date',
  type: 'date',
  sortable: true,
  width: 200,
};

const releaseGrantNameColumn: TableColumn = {
  field: 'grantName',
  header: 'Grant Name',
  sortable: true,
  width: 180,
};

export const releaseAwardNumberColumn: TableColumn = {
  field: 'awardNumber',
  header: 'Award ID',
  sortable: true,
  width: 180,
};

export const releaseAwardIdColumn: TableColumn = {
  field: 'awardId',
  header: 'Award ID',
  sortable: true,
  width: 180,
};

const releaseAwardTypeColumn: TableColumn = {
  field: 'awardType',
  header: 'Award Type',
  type: 'badge',
  sortable: true,
  width: 200,
  align: 'center',
  badge: {
    statusMap: { RSU: 'success', PSU: 'pink-tag' },
    defaultVariant: 'default',
  },
};

const releaseTaxCertColumn: TableColumn = {
  field: 'isTaxCertificationVerified',
  header: 'Tax Certification',
  type: 'custom',
  sortable: true,
  width: 250,
  align: 'center',
  renderer: 'releaseTaxCertification',
};

const releaseSettlementTypeColumn: TableColumn = {
  field: 'settlementType',
  header: 'Settlement Type',
  sortable: true,
  width: 180,
};

const releaseBrokerAccountColumn: TableColumn = {
  field: 'isBrokerAccountCreated',
  header: 'Broker Account Status',
  type: 'badge',
  sortable: false,
  width: 180,
  badge: {
    statusMap: { true: 'success', false: 'error' },
  },
  // Legacy server schema: conditionalValue at column level (BadgeField substitution).
  conditionalValues: { true: 'CREATED', false: 'NOT CREATED' },
};

const releaseAccountNumberColumn: TableColumn = {
  field: 'accountNumber',
  header: 'Account Number',
  sortable: false,
  width: 180,
};

const releaseTaxCollectionColumn: TableColumn = {
  field: 'taxCollection',
  header: 'Tax Collection',
  type: 'badge',
  sortable: true,
  width: 250,
  align: 'center',
  badge: {
    statusMap: { Pending: 'info', Completed: 'success', Reversed: 'primary' },
  },
  conditionalValues: {
    Pending: 'PENDING',
    Completed: 'COMPLETED',
    Reversed: 'REVERSED',
  },
};

/** Included tab column order (legacy releaseForm included tab). */
export const releaseIncludedItemColumns: TableColumn[] = [
  releaseParticipantDetailColumn,
  releaseVestDateColumn,
  releaseVestingValuesColumn,
  releaseFmvColumn,
  releaseVestingUnitColumn,
  releaseGrantNameColumn,
  releaseAwardNumberColumn,
  releaseAwardTypeColumn,
  releaseTaxCertColumn,
  releaseSettlementTypeColumn,
  releaseBrokerAccountColumn,
  releaseAccountNumberColumn,
];

/** Select-items tab — awardId accessor (vesting-list DTO). */
export const releaseSelectedItemColumns: TableColumn[] = [
  releaseParticipantDetailColumn,
  releaseVestDateColumn,
  releaseVestingValuesColumn,
  releaseFmvColumn,
  releaseVestingUnitColumn,
  releaseGrantNameColumn,
  releaseAwardIdColumn,
  releaseAwardTypeColumn,
  releaseTaxCertColumn,
  releaseSettlementTypeColumn,
  releaseBrokerAccountColumn,
  releaseAccountNumberColumn,
];

/** Release Details tab (view page) — 11 columns (excludes server-schema broker/account + employeeType/grantDate). */
export const releaseDetailsItemColumns: TableColumn[] = [
  releaseParticipantDetailColumn,
  { ...releaseFmvColumn, width: 200 },
  releaseVestingValuesColumn,
  releaseVestingUnitColumn,
  releaseGrantNameColumn,
  releaseAwardNumberColumn,
  releaseAwardTypeColumn,
  releaseVestDateColumn,
  releaseTaxCertColumn,
  releaseTaxCollectionColumn,
  releaseSettlementTypeColumn,
];

export function withReleaseIdEndpoint(
  schema: ExternalTableSchema,
  releaseId: string,
): ExternalTableSchema {
  return {
    ...schema,
    dataSource: schema.dataSource
      ? {
          ...schema.dataSource,
          endpoint: schema.dataSource.endpoint.replace('{id}', releaseId),
        }
      : schema.dataSource,
  };
}
